import { ArrowLeftRight, CircleAlert, PowerOff } from 'lucide-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { I18n } from '@/i18n/I18n';
import { DESKTOP_INSTRUMENT_STATE } from '@/types/instrumentBridgeTypes';

/**
 * The running / stopped / errored badge beside an instrument.
 *
 * Copied from veriform-desktop's `InstrumentStatus` (renderer/Instruments/
 * InstrumentStatus.tsx), with one thing left out: there, the errored badge is a
 * button opening `InstrumentTroubleShootingModal`, which restarts providers and
 * reads logs on that machine. None of that is reachable from here, so the badge
 * is not a button and the tooltip says only what went wrong.
 *
 * `I18n.get('Running')` has no entry in either app's strings, so both fall back
 * to the key and render the same word. Left as desktop has it so the two do not
 * drift apart over a capital letter.
 */

export function InstrumentStatus(props: { status?: DESKTOP_INSTRUMENT_STATE; error?: string }) {
  const { status, error } = props;

  if (status === DESKTOP_INSTRUMENT_STATE.ERROR) {
    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 250, hide: 400 }}
        overlay={error ? <Tooltip>{error}</Tooltip> : <div />}
      >
        <div className='error-badge'>
          <CircleAlert size={16} color='var(--red-600)' />
          {I18n.get('Errored')}
        </div>
      </OverlayTrigger>
    );
  }

  if (status === DESKTOP_INSTRUMENT_STATE.RUNNING) {
    return (
      <div className="running-badge" title={I18n.get('instrumentIsRunning')}>
        <ArrowLeftRight size={16} color='var(--green-600)' />
        {I18n.get('Running')}
      </div>
    );
  }

  return (
    <div className="stopped-badge" title={I18n.get('instrumentNotRunning')}>
      <PowerOff size={16} color='var(--neutral-600)' />
      {I18n.get('Stopped')}
    </div>
  );
}
